import styled from 'styled-components';

// Staff list container
export const StaffListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Empty state
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.primary};
`;

export const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

export const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
  max-width: 400px;
`;

// Header row for staff section
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StaffCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Staff card/row
export const StaffCard = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.card.borderHover};
    box-shadow: ${({ theme }) => theme.colors.card.shadowHover};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const StaffAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.2 : 0.1)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  font-weight: 600;
`;

export const StaffInfo = styled.div`
  flex: 1;
  margin-left: 16px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

export const StaffName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

export const StaffDetails = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

export const StaffDetail = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

export const StaffActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => theme.colors.card.border};
  }
`;

export const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ variant, theme }) =>
      variant === 'danger'
        ? theme.name === 'dark'
          ? 'rgba(239, 68, 68, 0.2)'
          : 'rgba(239, 68, 68, 0.1)'
        : theme.colors.primaryOpacity(theme.name === 'dark' ? 0.2 : 0.1)};
    border-color: ${({ variant, theme }) =>
      variant === 'danger' ? theme.colors.status.error : theme.colors.primary};
    color: ${({ variant, theme }) =>
      variant === 'danger' ? theme.colors.status.error : theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Add button styled
export const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary},
    #ff6b3b
  );
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primaryOpacity(0.3)};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Drawer form styles
export const DrawerContent = styled.div`
  padding: 24px;
`;

export const DrawerSection = styled.div`
  margin-bottom: 24px;
`;

export const DrawerSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const DrawerDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

export const FormGroup = styled.div`
  margin-bottom: 16px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

export const SearchResultsList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: 12px;
  max-height: 200px;
  overflow-y: auto;
`;

export const SearchResultItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) =>
      theme.colors.primaryOpacity(theme.name === 'dark' ? 0.1 : 0.05)};
  }
`;

export const SearchResultName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

export const SearchResultPhone = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

export const NoResultsMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  font-size: 14px;
`;

export const DrawerFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

// Delete confirmation modal
export const ConfirmationContent = styled.div`
  text-align: center;
  padding: 24px;
`;

export const ConfirmationIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ theme }) => theme.colors.status.error};
`;

export const ConfirmationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

export const ConfirmationText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
`;

export const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;
